import AsyncStorage from '@react-native-async-storage/async-storage';
import { UNIFIED_JOB_DATA } from '../services/UnifiedJobData';

/**
 * Unified Activity Tracker - Fixed Version
 * Tracks all user activities across the app and provides a centralized activity feed
 * Fixed date parsing issues and improved error handling
 */
class UnifiedActivityTracker {
  constructor() {
    this.initialized = false;
    this.activities = [];
    this.subscribers = {};
    this.storageKey = '@jobeng_activities';
  }

  /**
   * Safe date parsing function that handles multiple date formats
   */
  parseDate(dateInput) {
    if (!dateInput) {
      return new Date();
    }

    // If it's already a Date object
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? new Date() : dateInput;
    }

    // If it's a string, try different parsing methods
    if (typeof dateInput === 'string') {
      // Try ISO format first
      let parsedDate = new Date(dateInput);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }

      // Try parsing human-readable formats like "30 Jan 2025"
      const humanReadableFormats = [
        // DD MMM YYYY format
        /^(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})$/i,
        // DD/MM/YYYY format
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        // YYYY-MM-DD format
        /^(\d{4})-(\d{1,2})-(\d{1,2})$/
      ];

      const monthMap = {
        'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
        'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
      };

      // Try DD MMM YYYY format
      const match = dateInput.match(humanReadableFormats[0]);
      if (match) {
        const day = parseInt(match[1]);
        const month = monthMap[match[2].toLowerCase()];
        const year = parseInt(match[3]);
        
        if (month !== undefined && day >= 1 && day <= 31 && year >= 2020 && year <= 2030) {
          parsedDate = new Date(year, month, day);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
          }
        }
      }

      // If all parsing fails, return current date
      console.warn('⚠️ Could not parse date:', dateInput, 'using current date');
      return new Date();
    }

    // If it's a number (timestamp)
    if (typeof dateInput === 'number') {
      parsedDate = new Date(dateInput);
      return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    }

    // Fallback to current date
    return new Date();
  }

  /**
   * Initialize the activity tracker
   */
  async initialize() {
    try {
      console.log('🔄 Initializing Activity Tracker...');
      await this.loadActivities();
      this.initialized = true;
      console.log('✅ Activity Tracker initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Activity Tracker initialization failed:', error);
      return false;
    }
  }

  /**
   * Load activities from AsyncStorage
   */
  async loadActivities() {
    try {
      const stored = await AsyncStorage.getItem(this.storageKey);
      if (stored) {
        this.activities = JSON.parse(stored);
        console.log(`📱 Loaded ${this.activities.length} activities from storage`);
      } else {
        // Generate initial activities from existing app data
        await this.generateInitialActivities();
      }
    } catch (error) {
      console.error('❌ Error loading activities:', error);
      await this.generateInitialActivities();
    }
  }

  /**
   * Save activities to AsyncStorage
   */
  async saveActivities() {
    try {
      await AsyncStorage.setItem(this.storageKey, JSON.stringify(this.activities));
      console.log('💾 Activities saved to storage');
    } catch (error) {
      console.error('❌ Error saving activities:', error);
    }
  }

  /**
   * Generate initial activities from existing app data with safe date parsing
   */
  async generateInitialActivities() {
    console.log('🔄 Generating initial activities from app data...');
    
    const initialActivities = [];
    const now = new Date();

    try {
      // Generate activities from job applications
      UNIFIED_JOB_DATA.forEach((job, index) => {
        try {
          const baseDate = new Date(now.getTime() - (index * 24 * 60 * 60 * 1000)); // Spread over days

          // Safe date parsing for all job dates
          const appliedDate = this.parseDate(job.appliedDate || baseDate);
          const interviewDate = job.interviewDate ? this.parseDate(job.interviewDate) : null;
          const offerDate = job.offerDate ? this.parseDate(job.offerDate) : null;

          // Application submitted activity
          initialActivities.push({
            id: `activity_app_${job.id}`,
            type: 'application_submitted',
            title: 'Application Submitted',
            subtitle: `Applied for ${job.title}`,
            company: job.company,
            position: job.title,
            jobId: job.id,
            applicationId: job.applicationId,
            timestamp: appliedDate.toISOString(),
            date: this.formatDate(appliedDate),
            time: this.formatTime(appliedDate),
            status: 'completed',
            priority: job.featured ? 'high' : 'medium',
            category: 'application',
            icon: 'paper-plane',
            color: '#10B981',
            description: `Successfully submitted application for ${job.title} at ${job.company}`,
            metadata: {
              salary: job.salary,
              location: job.location,
              type: job.type,
              matchScore: job.matchScore,
              companyLogo: job.companyLogo,
              applicationScore: job.matchScore,
              resumeVersion: job.resumeVersion || 'Latest Resume',
              coverLetter: job.coverLetter ? 'Included' : 'Not included'
            }
          });

          // Status-based activities
          if (job.status === 'interview_scheduled' || job.status === 'interview_completed') {
            const interviewActivityDate = interviewDate || new Date(baseDate.getTime() + 2 * 24 * 60 * 60 * 1000);
            
            initialActivities.push({
              id: `activity_interview_${job.id}`,
              type: 'interview_scheduled',
              title: 'Interview Scheduled',
              subtitle: `${job.interviewType || 'Interview'} scheduled`,
              company: job.company,
              position: job.title,
              jobId: job.id,
              applicationId: job.applicationId,
              timestamp: interviewActivityDate.toISOString(),
              date: this.formatDate(interviewActivityDate),
              time: this.formatTime(interviewActivityDate),
              status: job.status === 'interview_completed' ? 'completed' : 'scheduled',
              priority: 'high',
              category: 'interview',
              icon: 'calendar',
              color: '#3B82F6',
              description: `Interview scheduled for ${job.title} position at ${job.company}`,
              metadata: {
                interviewType: job.interviewType || 'Technical Interview',
                interviewLocation: job.interviewLocation || 'TBD',
                interviewer: job.contactPerson?.name || 'TBD',
                duration: '60 minutes',
                companyLogo: job.companyLogo
              }
            });
          }

          if (job.status === 'offer_received') {
            const offerActivityDate = offerDate || new Date(baseDate.getTime() + 5 * 24 * 60 * 60 * 1000);
            
            initialActivities.push({
              id: `activity_offer_${job.id}`,
              type: 'offer_received',
              title: 'Job Offer Received',
              subtitle: `Offer for ${job.title}`,
              company: job.company,
              position: job.title,
              jobId: job.id,
              applicationId: job.applicationId,
              offerId: job.offerId,
              timestamp: offerActivityDate.toISOString(),
              date: this.formatDate(offerActivityDate),
              time: this.formatTime(offerActivityDate),
              status: 'pending',
              priority: 'high',
              category: 'offer',
              icon: 'gift',
              color: '#F59E0B',
              description: `Job offer received for ${job.title} position at ${job.company}`,
              metadata: {
                salary: job.salary,
                startDate: job.startDate,
                deadline: job.deadline,
                benefits: job.benefits?.slice(0, 3) || [],
                companyLogo: job.companyLogo,
                offerStatus: job.offerStatus || 'Pending'
              }
            });
          }

          if (job.status === 'rejected') {
            const rejectionDate = new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000);
            
            initialActivities.push({
              id: `activity_rejection_${job.id}`,
              type: 'application_rejected',
              title: 'Application Update',
              subtitle: `Application for ${job.title} not successful`,
              company: job.company,
              position: job.title,
              jobId: job.id,
              applicationId: job.applicationId,
              timestamp: rejectionDate.toISOString(),
              date: this.formatDate(rejectionDate),
              time: this.formatTime(rejectionDate),
              status: 'completed',
              priority: 'low',
              category: 'application',
              icon: 'close-circle',
              color: '#EF4444',
              description: `Application for ${job.title} at ${job.company} was not successful`,
              metadata: {
                reason: 'Position filled by another candidate',
                feedback: 'Strong application, encouraged to apply for future opportunities',
                companyLogo: job.companyLogo
              }
            });
          }
        } catch (jobError) {
          console.error('❌ Error processing job:', job.id, jobError);
          // Continue with other jobs even if one fails
        }
      });

      // Add portfolio activities with safe dates
      const portfolioActivities = await this.generatePortfolioActivities();
      initialActivities.push(...portfolioActivities);

      // Add assessment activities with safe dates
      const assessmentActivities = await this.generateAssessmentActivities();
      initialActivities.push(...assessmentActivities);

      // Sort by timestamp (newest first) with safe date comparison
      this.activities = initialActivities.sort((a, b) => {
        const dateA = this.parseDate(a.timestamp);
        const dateB = this.parseDate(b.timestamp);
        return dateB.getTime() - dateA.getTime();
      });
      
      await this.saveActivities();
      console.log(`✅ Generated ${this.activities.length} initial activities`);
    } catch (error) {
      console.error('❌ Error generating initial activities:', error);
      // Ensure we have at least some activities even if generation fails
      this.activities = [];
    }
  }

  /**
   * Generate portfolio-related activities with safe dates
   */
  async generatePortfolioActivities() {
    const activities = [];
    const now = new Date();

    try {
      // Portfolio updates
      activities.push({
        id: 'activity_portfolio_update_1',
        type: 'portfolio_updated',
        title: 'Portfolio Updated',
        subtitle: 'Added new project to portfolio',
        timestamp: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        date: this.formatDate(new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)),
        time: this.formatTime(new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)),
        status: 'completed',
        priority: 'medium',
        category: 'portfolio',
        icon: 'briefcase',
        color: '#8B5CF6',
        description: 'Added E-commerce Platform project to your portfolio',
        metadata: {
          projectName: 'E-commerce Platform',
          technologies: ['React', 'Node.js', 'MongoDB'],
          type: 'project_added'
        }
      });

      // Skill assessment
      activities.push({
        id: 'activity_skill_assessment_1',
        type: 'skill_assessment',
        title: 'Skill Assessment Completed',
        subtitle: 'React.js Assessment - Score: 85%',
        timestamp: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        date: this.formatDate(new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)),
        time: this.formatTime(new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000)),
        status: 'completed',
        priority: 'medium',
        category: 'assessment',
        icon: 'school',
        color: '#06B6D4',
        description: 'Completed React.js skill assessment with excellent results',
        metadata: {
          skill: 'React.js',
          score: 85,
          level: 'Advanced',
          duration: '45 minutes',
          questionsAnswered: 20
        }
      });
    } catch (error) {
      console.error('❌ Error generating portfolio activities:', error);
    }

    return activities;
  }

  /**
   * Generate assessment and learning activities with safe dates
   */
  async generateAssessmentActivities() {
    const activities = [];
    const now = new Date();

    try {
      // Learning activities
      activities.push({
        id: 'activity_learning_1',
        type: 'course_completed',
        title: 'Course Completed',
        subtitle: 'Advanced React Patterns',
        timestamp: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        date: this.formatDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)),
        time: this.formatTime(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)),
        status: 'completed',
        priority: 'medium',
        category: 'learning',
        icon: 'book',
        color: '#10B981',
        description: 'Successfully completed Advanced React Patterns course',
        metadata: {
          courseName: 'Advanced React Patterns',
          provider: 'Tech Academy',
          duration: '8 hours',
          certificateEarned: true,
          grade: 'A'
        }
      });

      // Webinar registration
      activities.push({
        id: 'activity_webinar_1',
        type: 'webinar_registered',
        title: 'Webinar Registration',
        subtitle: 'AI in Software Development',
        timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        date: this.formatDate(new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)),
        time: this.formatTime(new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000)),
        status: 'scheduled',
        priority: 'medium',
        category: 'learning',
        icon: 'videocam',
        color: '#3B82F6',
        description: 'Registered for AI in Software Development webinar',
        metadata: {
          webinarTitle: 'AI in Software Development',
          presenter: 'Dr. Sarah Chen',
          scheduledDate: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
          duration: '90 minutes'
        }
      });
    } catch (error) {
      console.error('❌ Error generating assessment activities:', error);
    }

    return activities;
  }

  /**
   * Add a new activity with safe date handling
   */
  async addActivity(activityData) {
    try {
      const now = new Date();
      const activityDate = this.parseDate(activityData.timestamp || now);
      
      const activity = {
        id: activityData.id || `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: activityDate.toISOString(),
        date: activityData.date || this.formatDate(activityDate),
        time: activityData.time || this.formatTime(activityDate),
        status: activityData.status || 'completed',
        priority: activityData.priority || 'medium',
        ...activityData
      };

      this.activities.unshift(activity); // Add to beginning (newest first)
      await this.saveActivities();
      
      // Notify subscribers
      this.emit('activity_added', activity);
      
      console.log('✅ Activity added:', activity.title);
      return activity;
    } catch (error) {
      console.error('❌ Error adding activity:', error);
      return null;
    }
  }

  /**
   * Get all activities with optional filtering
   */
  getActivities(filters = {}) {
    let filteredActivities = [...this.activities];

    // Filter by category
    if (filters.category && filters.category !== 'all') {
      filteredActivities = filteredActivities.filter(activity => 
        activity.category === filters.category
      );
    }

    // Filter by status
    if (filters.status && filters.status !== 'all') {
      filteredActivities = filteredActivities.filter(activity => 
        activity.status === filters.status
      );
    }

    // Filter by priority
    if (filters.priority && filters.priority !== 'all') {
      filteredActivities = filteredActivities.filter(activity => 
        activity.priority === filters.priority
      );
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredActivities = filteredActivities.filter(activity => 
        activity.title.toLowerCase().includes(searchTerm) ||
        activity.subtitle?.toLowerCase().includes(searchTerm) ||
        activity.company?.toLowerCase().includes(searchTerm) ||
        activity.description?.toLowerCase().includes(searchTerm)
      );
    }

    // Date range filter with safe date parsing
    if (filters.dateRange) {
      const now = new Date();
      let startDate;
      
      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filteredActivities = filteredActivities.filter(activity => {
          const activityDate = this.parseDate(activity.timestamp);
          return activityDate >= startDate;
        });
      }
    }

    return filteredActivities;
  }

  /**
   * Get activity by ID
   */
  getActivityById(id) {
    return this.activities.find(activity => activity.id === id);
  }

  /**
   * Get activity statistics
   */
  getStatistics() {
    const total = this.activities.length;
    const categories = {};
    const statuses = {};
    const priorities = {};

    this.activities.forEach(activity => {
      // Count by category
      categories[activity.category] = (categories[activity.category] || 0) + 1;
      
      // Count by status
      statuses[activity.status] = (statuses[activity.status] || 0) + 1;
      
      // Count by priority
      priorities[activity.priority] = (priorities[activity.priority] || 0) + 1;
    });

    return {
      total,
      categories,
      statuses,
      priorities,
      recent: this.activities.filter(activity => {
        const activityDate = this.parseDate(activity.timestamp);
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return activityDate >= weekAgo;
      }).length
    };
  }

  /**
   * Update activity status
   */
  async updateActivity(id, updates) {
    try {
      const index = this.activities.findIndex(activity => activity.id === id);
      if (index !== -1) {
        this.activities[index] = { ...this.activities[index], ...updates };
        await this.saveActivities();
        this.emit('activity_updated', this.activities[index]);
        console.log('✅ Activity updated:', id);
        return this.activities[index];
      }
      return null;
    } catch (error) {
      console.error('❌ Error updating activity:', error);
      return null;
    }
  }

  /**
   * Delete activity
   */
  async deleteActivity(id) {
    try {
      const index = this.activities.findIndex(activity => activity.id === id);
      if (index !== -1) {
        const deleted = this.activities.splice(index, 1)[0];
        await this.saveActivities();
        this.emit('activity_deleted', deleted);
        console.log('✅ Activity deleted:', id);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Error deleting activity:', error);
      return false;
    }
  }

  /**
   * Track job application activity
   */
  async trackJobApplication(jobData) {
    return await this.addActivity({
      type: 'application_submitted',
      title: 'Application Submitted',
      subtitle: `Applied for ${jobData.title}`,
      company: jobData.company,
      position: jobData.title,
      jobId: jobData.id,
      applicationId: jobData.applicationId,
      category: 'application',
      icon: 'paper-plane',
      color: '#10B981',
      description: `Successfully submitted application for ${jobData.title} at ${jobData.company}`,
      metadata: {
        salary: jobData.salary,
        location: jobData.location,
        type: jobData.type,
        matchScore: jobData.matchScore,
        companyLogo: jobData.companyLogo
      }
    });
  }

  /**
   * Track interview activity
   */
  async trackInterview(interviewData) {
    return await this.addActivity({
      type: 'interview_scheduled',
      title: 'Interview Scheduled',
      subtitle: `${interviewData.type || 'Interview'} scheduled`,
      company: interviewData.company,
      position: interviewData.position,
      jobId: interviewData.jobId,
      category: 'interview',
      icon: 'calendar',
      color: '#3B82F6',
      description: `Interview scheduled for ${interviewData.position} position at ${interviewData.company}`,
      metadata: {
        interviewType: interviewData.type,
        interviewLocation: interviewData.location,
        interviewer: interviewData.interviewer,
        duration: interviewData.duration || '60 minutes'
      }
    });
  }

  /**
   * Track offer activity
   */
  async trackOffer(offerData) {
    return await this.addActivity({
      type: 'offer_received',
      title: 'Job Offer Received',
      subtitle: `Offer for ${offerData.position}`,
      company: offerData.company,
      position: offerData.position,
      jobId: offerData.jobId,
      offerId: offerData.offerId,
      category: 'offer',
      icon: 'gift',
      color: '#F59E0B',
      description: `Job offer received for ${offerData.position} position at ${offerData.company}`,
      metadata: {
        salary: offerData.salary,
        startDate: offerData.startDate,
        deadline: offerData.deadline,
        benefits: offerData.benefits
      }
    });
  }

  /**
   * Track portfolio activity
   */
  async trackPortfolioUpdate(updateData) {
    return await this.addActivity({
      type: 'portfolio_updated',
      title: 'Portfolio Updated',
      subtitle: updateData.subtitle || 'Portfolio changes made',
      category: 'portfolio',
      icon: 'briefcase',
      color: '#8B5CF6',
      description: updateData.description || 'Your portfolio has been updated',
      metadata: updateData.metadata || {}
    });
  }

  /**
   * Track assessment activity
   */
  async trackAssessment(assessmentData) {
    return await this.addActivity({
      type: 'skill_assessment',
      title: 'Skill Assessment Completed',
      subtitle: `${assessmentData.skill} Assessment - Score: ${assessmentData.score}%`,
      category: 'assessment',
      icon: 'school',
      color: '#06B6D4',
      description: `Completed ${assessmentData.skill} skill assessment`,
      metadata: {
        skill: assessmentData.skill,
        score: assessmentData.score,
        level: assessmentData.level,
        duration: assessmentData.duration
      }
    });
  }

  /**
   * Event subscription system
   */
  subscribe(event, callback) {
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }
    this.subscribers[event].push(callback);
    
    return () => {
      this.subscribers[event] = this.subscribers[event].filter(cb => cb !== callback);
    };
  }

  emit(event, data) {
    if (this.subscribers[event]) {
      this.subscribers[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('❌ Error in event callback:', error);
        }
      });
    }
  }

  /**
   * Utility functions with safe date handling
   */
  formatDate(date) {
    try {
      const parsedDate = this.parseDate(date);
      const now = new Date();
      const diffTime = Math.abs(now - parsedDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      
      return parsedDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: parsedDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      console.error('❌ Error formatting date:', error);
      return 'Unknown date';
    }
  }

  formatTime(date) {
    try {
      const parsedDate = this.parseDate(date);
      return parsedDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.error('❌ Error formatting time:', error);
      return '00:00';
    }
  }

  /**
   * Clear all activities (for testing/reset)
   */
  async clearAllActivities() {
    try {
      this.activities = [];
      await AsyncStorage.removeItem(this.storageKey);
      this.emit('activities_cleared');
      console.log('✅ All activities cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing activities:', error);
      return false;
    }
  }
}

// Create and export singleton instance
const activityTracker = new UnifiedActivityTracker();
export default activityTracker;

// Export class for testing
export { UnifiedActivityTracker };

